import { Button } from 'tio-ui/components/buttons';
import { hash } from '@ember/helper';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { Section } from 'tio-ui/components/layout';
import { PhCheck, PhCircle } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import ForgivenessEstimatorResultsDashboardTable from 'tio-employee/components/forgiveness-estimator/results-dashboard-table';
import RecommendationsDashRecommendations from 'tio-employee/components/recommendations/dash-recommendations';
import QualifyingPaymentTracker from 'tio-employee/components/pslf/dashboard/qualifying-payment-tracker';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type AccountModel from 'tio-common/models/account';
import type PersonModel from 'tio-common/models/person';
import type PslfProfileModel from 'tio-common/models/pslf-profile';
import type RecomendationModel from 'tio-common/models/recommendation';
import { on } from '@ember/modifier';
import PslfMyDocumentsMyFormsTable from 'tio-employee/components/pslf/my-documents/my-forms-table';
import type UploadProviderLogicService from 'tio-employee/services/upload-provider-logic';

function submittedForms(pslfProfile: PslfProfileModel) {
  const submittedStates = ['submitted', 're_submitted'];
  return pslfProfile.pslfForms.slice().filter((form) => submittedStates.includes(form.status));
}

interface S {
  Args: {
    model: {
      loanSummary: LoanSummary;
      accounts: AccountModel[];
      pslfProfile: PslfProfileModel;
      person: PersonModel;
      mostRecentRecommendation: RecomendationModel;
    };
  };
}

export default class PslfDashboardIndexComponent extends Component<S> {
  @service declare uploadProviderLogic: UploadProviderLogicService;

  get uploadQueryParams() {
    return this.uploadProviderLogic.getUploadParams({
      isSlr: false,
      hasSyf: false,
      hasPslf: true,
      isWellnessOnly: false,
    });
  }

  <template>
    <Section>
      <:header>{{t "pslf.dashboard.get_started"}}</:header>
      <:subHeader>{{t "pslf.dashboard.optimize_your_pslf"}}</:subHeader>
      <:body>
        <ul role="list" class="divide-y divide-gray-100">
          <li class="flex justify-between gap-x-36 py-5">
            <div class="flex min-w-0 gap-x-4">
              {{#if @model.accounts.length}}
                <PhCheck @size="20" class="text-green-600" />
              {{else}}
                <PhCircle @weight="fill" @size="20" class="text-gray-400" />
              {{/if}}
              <div class="min-w-0 flex-auto">
                <p class="font-semibold text-gray-900">{{t "pslf.dashboard.add_loans_title"}}</p>
                <p class="mt-1 text-gray-500">{{t "pslf.dashboard.add_loans_description"}}</p>

              </div>
            </div>
            <div class="shrink-0 sm:items-end">
              <Button
                {{on
                  "click"
                  (transitionTo
                    "authenticated.observability.upload" (hash queryParams=this.uploadQueryParams)
                  )
                }}
                @intent="primary"
                @appearance="outlined"
              >
                {{t "pslf.dashboard.add_loans" numLoans=@model.accounts.length}}
              </Button>
            </div>
          </li>
          <li class="flex justify-between gap-x-36 py-5">
            <div class="flex min-w-0 gap-x-4">
              {{#if (submittedForms @model.pslfProfile)}}
                <PhCheck @size="20" class="text-green-600" />
              {{else}}
                <PhCircle @weight="fill" @size="20" class="text-gray-400" />
              {{/if}}
              <div class="min-w-0 flex-auto">
                <p class="font-semibold text-gray-900">{{t "pslf.dashboard.submit_your_form"}}</p>
                <p class="mt-1 text-gray-500">{{t "pslf.dashboard.track_payments"}} </p>
              </div>
            </div>
            <div class="shrink-0 sm:items-end">
              <Button
                {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
                @intent="primary"
                @appearance="outlined"
              >
                {{t "pslf.dashboard.form"}}
              </Button>
            </div>
          </li>
          <li class="flex justify-between gap-x-36 py-5">
            <div class="flex min-w-0 gap-x-4">
              {{#if @model.mostRecentRecommendation}}
                <PhCheck @size="20" class="text-green-600" />
              {{else}}
                <PhCircle @weight="fill" @size="20" class="text-gray-400" />
              {{/if}}
              <div class="min-w-0 flex-auto">
                <p class="font-semibold text-gray-900">{{t "pslf.dashboard.try_estimator"}}</p>
                <p class="mt-1 text-gray-500">{{t "pslf.dashboard.try_estimator_description"}}</p>
              </div>
            </div>
            <div class="shrink-0 sm:items-end">
              <Button
                {{on
                  "click"
                  (transitionTo "authenticated.pslf.dashboard.forgiveness-estimator.index")
                }}
                @intent="primary"
                @appearance="outlined"
              >{{t "pslf.dashboard.estimator"}}</Button>
            </div>
          </li>
        </ul>
      </:body>
    </Section>

    <Section class="overflow-x-auto">
      <:header>{{t "pslf.document_repo.my_pslf_forms"}}</:header>
      <:body>
        <PslfMyDocumentsMyFormsTable @limit={{10}} @employee={{@model.pslfProfile.employee}} />
      </:body>
    </Section>

    <QualifyingPaymentTracker @accounts={{@model.accounts}} />

    <RecommendationsDashRecommendations
      @loanSummary={{@model.loanSummary}}
      @person={{@model.person}}
      @recommendation={{@model.mostRecentRecommendation}}
      @source="pslf"
    />
    {{#if @model.mostRecentRecommendation}}
      <ForgivenessEstimatorResultsDashboardTable
        @recommendation={{@model.mostRecentRecommendation}}
        @programType="pslf"
      />
    {{/if}}
  </template>
}
